import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAllUsersGroupedByTenant,
  promoteToSuperAdmin,
  promoteToGlobalPhysician,
  getRoleDisplay
} from '../../services/roleManagementService';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import WarningIcon from '@mui/icons-material/Warning';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

const UserRoleManagement = () => {
  const { profile, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [usersGrouped, setUsersGrouped] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    user: null
  });
  const [processing, setProcessing] = useState(false);

  // Load users
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    const result = await getAllUsersGroupedByTenant();
    if (result.data) {
      setUsersGrouped(result.data);
    }
    setLoading(false);
  };

  // Filter users by search term
  const filterUsers = (users) => {
    if (!searchTerm) return users;
    
    return users.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      return (
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.tenant_role?.toLowerCase().includes(searchLower)
      );
    });
  };

  const openPromoteDialog = (type, user) => {
    setConfirmDialog({
      open: true,
      type,
      user
    });
  };

  const closeDialog = () => {
    setConfirmDialog({
      open: false,
      type: null,
      user: null
    });
  };

  const handlePromote = async () => {
    if (!confirmDialog.user || !profile) return;

    setProcessing(true);
    
    let result;
    if (confirmDialog.type === 'super_admin') {
      result = await promoteToSuperAdmin(confirmDialog.user.id);
    } else if (confirmDialog.type === 'global_physician') {
      result = await promoteToGlobalPhysician(confirmDialog.user.id);
    }

    setProcessing(false);
    
    if (result?.data) {
      closeDialog();
      await loadUsers(); // Refresh the list
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuperAdmin) {
    return (
      <Alert severity="error">
        You must be a Super Admin to manage user roles.
      </Alert>
    );
  }

  return (
    <Box sx={{ px: 2 }}>
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25, fontSize: '1.125rem' }}>
            User Role Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Manage user roles and promote users to platform-level access
          </Typography>
        </Box>
        
        <TextField
          size="small"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {Object.entries(usersGrouped).map(([tenantId, { tenant, users }]) => {
        const filteredUsers = filterUsers(users);
        
        if (filteredUsers.length === 0 && searchTerm) return null;

        return (
          <Accordion key={tenantId} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon color="primary" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {tenant?.name || 'No Tenant'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {tenant?.subdomain && `${tenant.subdomain} • `}
                    {tenant?.location_city && `${tenant.location_city}, `}
                    {tenant?.location_country}
                    {' • '}
                    {filteredUsers.length} users
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Access Level</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((user) => {
                      const roleInfo = getRoleDisplay(user);
                      const canPromoteToSuperAdmin = user.platform_role !== 'super_admin';
                      const canPromoteToGlobalPhysician = 
                        user.tenant_role === 'physician' && 
                        user.platform_role !== 'global_physician' &&
                        user.platform_role !== 'super_admin';

                      return (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {user.first_name} {user.last_name}
                              </Typography>
                              {user.id === profile.id && (
                                <Chip label="You" size="small" color="info" sx={{ mt: 0.5 }} />
                              )}
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Chip
                              label={`${roleInfo.icon} ${roleInfo.label}`}
                              color={roleInfo.color}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          
                          <TableCell>
                            <Chip
                              label={roleInfo.access}
                              variant="outlined"
                              size="small"
                              color={roleInfo.level === 'platform' ? 'success' : 'default'}
                            />
                          </TableCell>
                          
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              {canPromoteToSuperAdmin && (
                                <Tooltip title="Promote to Super Admin (Platform-wide access)">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<AdminPanelSettingsIcon />}
                                    onClick={() => openPromoteDialog('super_admin', user)}
                                    disabled={user.id === profile.id}
                                  >
                                    Super Admin
                                  </Button>
                                </Tooltip>
                              )}
                              
                              {canPromoteToGlobalPhysician && (
                                <Tooltip title="Make Global Physician (Cross-clinic access)">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<PublicIcon />}
                                    onClick={() => openPromoteDialog('global_physician', user)}
                                  >
                                    Global
                                  </Button>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirm Role Promotion
        </DialogTitle>
        
        <DialogContent>
          {confirmDialog.type === 'super_admin' && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                You are about to promote <strong>{confirmDialog.user?.first_name} {confirmDialog.user?.last_name}</strong> to <strong>Super Admin</strong>.
              </DialogContentText>
              
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  ⚠️ This will grant platform-wide access:
                </Typography>
                <Typography variant="body2" component="div">
                  • View and manage ALL clinics<br />
                  • Access ALL patient data<br />
                  • Approve physicians and sponsors<br />
                  • Create and manage clinics<br />
                  • Promote other users to admin roles
                </Typography>
              </Alert>
              
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Tenant Change:</strong> This user will be moved to the Alpha01 Platform tenant.
                </Typography>
              </Alert>
            </>
          )}
          
          {confirmDialog.type === 'global_physician' && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                You are about to make <strong>{confirmDialog.user?.first_name} {confirmDialog.user?.last_name}</strong> a <strong>Global Physician</strong>.
              </DialogContentText>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  🌍 This will grant cross-clinic access:
                </Typography>
                <Typography variant="body2" component="div">
                  • See patients from ANY clinic<br />
                  • Conduct consultations across all locations<br />
                  • Write prescriptions for any patient<br />
                  • Part of Alpha01's global physician pool
                </Typography>
              </Alert>
              
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Tenant Change:</strong> This physician will be moved to the Alpha01 Platform tenant.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handlePromote}
            variant="contained"
            color={confirmDialog.type === 'super_admin' ? 'error' : 'primary'}
            disabled={processing}
            startIcon={processing && <CircularProgress size={20} />}
          >
            {processing ? 'Promoting...' : 'Confirm Promotion'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserRoleManagement;
